// @ts-strict-ignore
import type { SerializedStyles } from "@emotion/react";
import type { MutableRefObject, ReactNode, ReactElement } from "react";
import { useEffect, useState } from "react";

import { Typography, Skeleton } from "@aviary";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { useCatalogSearch } from "@shared/hooks/useCatalogSearch/useCatalogSearch";
import { useCatalogSearchResultTitle } from "@shared/hooks/useCatalogSearchResultTitle/useCatalogSearchResultTitle";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";
import { useBannersHeight } from "@shared/reactiveVar/useBannersHeight/useBannersHeight.store";

import { ListGridSelector } from "../ListGridSelector/ListGridSelector";

import { useStickyProductResultsHeaderBreakpoints } from "./useStickyProductResultsHeaderBreakpoints";

import * as styles from "./StickyProductResultsHeader.styles";

interface Props {
  title?: string | (ReactElement | string)[];
  resultCount: number;
  displayedCount: number;
  isLoadingNewResults: boolean;
  scrollingRef?: MutableRefObject<any>;
  children: ReactNode;
  isDrawer?: boolean;
  isCareCenter?: boolean;
  hideGridSelector?: boolean;
  headerStyle?: SerializedStyles;
}
const StickyProductResultsHeader = ({
  title,
  resultCount,
  displayedCount,
  scrollingRef,
  isLoadingNewResults,
  isDrawer,
  children,
  hideGridSelector,
  isCareCenter = false,
  headerStyle,
}: Props) => {
  const { phoneLarge, tablet } = useBreakpoints();
  const { bannersHeight } = useBannersHeight();

  const isSmallPhone = phoneLarge.lessThan;
  const isLargePhone = !isSmallPhone && phoneLarge.active;
  const isTablet = !isLargePhone && tablet.active;
  const [isPastBreakpoint, setIsPassedBreakpoint] = useState(false);
  const { searchQuery, isGridView, setGridView } = useCatalogSearch();
  const { isUnauthenticated, isPatient, isEmerson } = useSharedGlobalConfig();
  const isSortViewSelectorDisabled = !resultCount || resultCount <= 1;
  const searchResultTitle = useCatalogSearchResultTitle(searchQuery, displayedCount, resultCount);
  const breakpoint = useStickyProductResultsHeaderBreakpoints({
    isDrawer,
    isPatient,
    bannersHeight,
  });
  useEffect(() => {
    if (scrollingRef) {
      const scrollableDrawerDiv = scrollingRef?.current?.parentElement;

      const handleDivScroll = () => {
        const scrolled = scrollableDrawerDiv?.scrollTop;

        if (scrolled > breakpoint) {
          setIsPassedBreakpoint(true);
        } else {
          setIsPassedBreakpoint(false);
        }
      };

      scrollableDrawerDiv?.addEventListener("scroll", handleDivScroll, { passive: true });

      return () => scrollableDrawerDiv?.removeEventListener("scroll", handleDivScroll);
    } else {
      const handleWindowScroll = () => {
        const scrolled = window.scrollY;

        if (scrolled > breakpoint) {
          setIsPassedBreakpoint(true);
        } else {
          setIsPassedBreakpoint(false);
        }
      };

      window?.addEventListener("scroll", handleWindowScroll, { passive: true });

      return () => window?.removeEventListener("scroll", handleWindowScroll);
    }
  }, [scrollingRef, isSmallPhone, isLargePhone, isTablet]);

  if (isCareCenter) {
    return null;
  }

  const headerStyles = [
    styles.stickyHeader,
    isPastBreakpoint && styles.showStickyHeader,
    isUnauthenticated && !isEmerson && styles.isUnauthenticated,
    isPatient && styles.isPatient,
    scrollingRef && styles.stickyHeaderDrawer,
    isDrawer && styles.drawerHeader,
    bannersHeight && styles.bannersAdjust(bannersHeight),
    headerStyle,
  ];

  const innerMenuStyles = [styles.innerMenu, scrollingRef && styles.innerMenuDrawer];

  const renderResultCount = () => {
    if (isLoadingNewResults) {
      return <Skeleton type="typography" typographySize="h1" />;
    }

    if (title) {
      return <Typography css={styles.resultsText}>{title}</Typography>;
    }

    return <Typography css={styles.resultsText}>{searchResultTitle}</Typography>;
  };

  const renderGridSelector = () =>
    !hideGridSelector && (
      <ListGridSelector
        isDisabled={isSortViewSelectorDisabled}
        isGridView={isGridView}
        handleToggle={setGridView}
        css={styles.gridListSelector}
      />
    );

  return (
    <div css={headerStyles} data-testid="sticky-search-header">
      <div css={innerMenuStyles}>
        {renderResultCount()}
        <div css={styles.rightOptions}>
          <span css={styles.children}>{children}</span>
          {renderGridSelector()}
        </div>
      </div>
    </div>
  );
};

export { StickyProductResultsHeader };
